import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { AuthLayout } from "./Login";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState(location.state?.email || "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: newPassword });
      toast.success("Password reset. You can sign in now.");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="text-xs uppercase tracking-[0.24em] text-cyan-400 mb-3">Set new password</div>
      <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">Reset password</h1>
      <p className="text-sm text-zinc-400 mt-2">Enter the 6-digit code from your email and choose a new password.</p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
          <Input data-testid="reset-email" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="h-12 bg-white/5 border-white/10" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Reset code</Label>
          <div className="flex justify-center">
            <InputOTP data-testid="reset-otp" maxLength={6} value={code} onChange={setCode}>
              <InputOTPGroup>
                {[0,1,2,3,4,5].map(i => (
                  <InputOTPSlot key={i} data-testid={`reset-otp-slot-${i}`} index={i} className="h-12 w-11 text-lg bg-white/5 border-white/10 text-white" />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="np" className="text-xs uppercase tracking-widest text-zinc-500">New password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input data-testid="reset-newpass" id="np" type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="h-12 pl-10 bg-white/5 border-white/10 focus-visible:ring-cyan-500/40 focus-visible:border-cyan-500/60" />
          </div>
        </div>
        <Button data-testid="reset-submit" type="submit" disabled={loading} className="btn-primary w-full h-12 rounded-full font-semibold text-base">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
        </Button>
      </form>

      <div className="mt-6 text-sm text-center text-zinc-400">
        <Link to="/login" data-testid="reset-back-login" className="text-fuchsia-400 hover:text-fuchsia-300 font-medium">Back to sign in</Link>
      </div>
    </AuthLayout>
  );
}
